import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Api from "../../services/Api";
import {getLanguage, showLanguageString} from "../../lang/helpers";
import iconCalendar from "url:shared-assets/icons/calendar-2.svg"
import iconActivities from "url:shared-assets/icons/activities.svg";
import iconPlant from "url:shared-assets/icons/plant.svg";
import iconScale from "url:shared-assets/icons/scale.svg";
import iconAi from "url:shared-assets/icons/ai.svg";
import iconPlus from "url:shared-assets/icons/white/plus.svg";
import iconPlusBlack from "url:shared-assets/icons/black/plus.svg";
import iconArrowRight from "url:shared-assets/icons/black/arrow-right.svg";
import iconArrowWhiteRight from "url:shared-assets/icons/white/arrow-right.svg";
import iconCircleRings from "url:shared-assets/icons/white/circle-rings.svg";
import {useNavigate} from "react-router";
import {Modal, ModalBody, ModalHeader} from "../../components/Modal";
import Loading from "../../components/Loading";
import CustomButton from "../../components/CustomButton"
import PlainBadge from "../../components/Badges/PlainBadge";
import {VisionStatementInterface, VisionStatementStatus} from "./interfaces";
import VisionStatementSummary from "./components/VisionStatementSummary";
import VisionStatementDrafts from "./components/VisionStatementDrafts";
import TabMenu from "../../components/TabMenu/TabMenu";
import {FeedbackType} from "../SurveyResult/interfaces";
import AttentionBox from "../../components/Boxes/AttentionBox";

interface SurveyRequestInterface {
  id: string;
  created: string;
  deadline: string;
  completed: string;
  status: string;
}

interface SurveyInterface {
  id: string;
  name: string;
  canCreateRequest: boolean;
  canInvite: boolean;
  pendingResponseId?: string;
  lastReportId?: string;
  surveyRequest?: SurveyRequestInterface;
  visionStatements: Array<VisionStatementInterface>;
  feedbackType: FeedbackType
}

interface CustomerCategoryInterface {
  id: string;
  name: string;
  name_sv: string;
  description_en: string;
  description_sv: string;
  intro_self_assessment_en: string;
  intro_self_assessment_sv: string;
  intro_create_vs_en: string;
  intro_create_vs_sv: string;
  surveys: Array<SurveyInterface>
  visionStatements: Array<VisionStatementInterface>;
}

interface OngoingActivityInterface {
  id: string;
  type: string;
  title: string;
  deadline?: string;
  completed: boolean;
}

const DevelopmentPlan = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState(false);
  const [customerCategories, setCustomerCategories] = useState<Array<CustomerCategoryInterface>>([]);
  const [selectDrafts, setSelectDrafts] = useState<SurveyInterface | null>(null);
  const [ongoingActivities, setOngoingActivities] = useState<Array<OngoingActivityInterface>>([]);
  const [surveyStatus, setSurveyStatus] = useState<Array<string>>([]);
  const [showActiveStandalone, setShowActiveStandalone] = useState(true);

  let mainButtons = 0;    // Enumerate the main buttons and make sure only the first is primary

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (customerCategories.length > 0 && window.location.hash) {
      const hashParts = window.location.hash.split('_');
      if (hashParts.length === 2) {
        const what = hashParts[0];
        const hashId = hashParts[1];
        if (what === "#drafts") {
          openDrafts(hashId);
        }
        history.pushState("", document.title, window.location.pathname);
      }
    }
  }, [customerCategories])

  const openDrafts = (_surveyId: string) => {
    for (const category of customerCategories) {
      if (category.surveys.length > 0) {
        for (const survey of category.surveys) {
          if (survey.id === _surveyId) {
            setSelectDrafts(survey);
            return;
          }
        }
      }
    }
  }

  const getData = () => {
    api.get("client/dev-plan").then(response => {
      setCustomerCategories(response.json.customerCategories);
      setOngoingActivities(response.json.ongoingActivities);
      setLoaded(true);
    })
  }

  if (!loaded) {
    return <Loading />;
  }

  const renderCustomerCategorySurveys = (customerCategory: CustomerCategoryInterface)=> {
    return (
      <div key={"cat_" + customerCategory.id}>
        {
          customerCategory.surveys.map(survey => {
            return (
              <div key={"category_survey_" + survey.id}>
                {renderCustomerCategorySurvey(customerCategory, survey)}
              </div>
            )
          })
        }
      </div>
    )
  }

  const renderCustomerCategorySurvey = (customerCategory: CustomerCategoryInterface, survey: SurveyInterface) => {
    let lastSurveyRequest: SurveyRequestInterface | undefined = undefined;

    if (survey.surveyRequest) {
      lastSurveyRequest = survey.surveyRequest;
    }
    let showSelectDrafts = survey.visionStatements.length > 0;

    return (
      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-indigo-900">
              <img src={iconCalendar} alt="ico" className="h-6 w-6"/>
            </div>
            <p className="z-h3 ml-2">
              {showLanguageString(lang, customerCategory.name_sv, customerCategory.name)}
              {
                customerCategory.surveys.length > 1 && <>: {survey.name}</>
              }
            </p>
          </div>

          <div className="flex">
            {
              survey.lastReportId &&
                <div>
                    <CustomButton
                        buttonType="transparentBase"
                        size="sm"
                        onClick={() => navigate("/dev-plan/survey/result/" + survey.lastReportId + "/summary")}
                        leftIcon={<img src={iconCalendar} alt="info"  className="h-4 w-4"/>}
                        style={{ width: "unset" }}
                    >
                      {lang === "sv" ? "Se insiktsrapport" : "See the insights report"}
                    </CustomButton>
                </div>

            }
            {
              showSelectDrafts &&
                <div>
                    <CustomButton
                        buttonType="transparentBase"
                        className="mx-auto cursor-pointer"
                        size="sm"
                        onClick={() => setSelectDrafts(survey)}
                        leftIcon={<img src={iconPlus} alt="info"  className="h-4 w-4"/>}
                        style={{ width: "unset" }}
                    >
                      {lang === "sv" ? "Lägg till mål" : "Add new goal"}
                    </CustomButton>
                </div>
            }
          </div>
        </div>



        <div className="mt-3">
          <TabMenu
            active={surveyStatus.indexOf(survey.id) > -1 ? 1 : 0}
            items={[
              {
                title: t("ongoing"),
                callback: () => changeTab(survey.id,0),
              },
              {
                title: t("completed"),
                callback: () => changeTab(survey.id, 1),
              },
            ]}
          />
        </div>

        {
          survey.visionStatements.length > 0 ? renderVisionStatements(survey) :
            renderSurveyRequest(survey, lastSurveyRequest)
        }

      </div>
    )
  }

  const changeTab = (surveyId: string, nr: number) => {
    if (nr > 0) {
      setSurveyStatus([...surveyStatus, surveyId]);
    } else {
      const _surveyStatus = [];
      for (let sId of surveyStatus) {
        if (sId !== surveyId) {
          _surveyStatus.push(sId);
        }
      }
      setSurveyStatus(_surveyStatus);
    }
  }

  const createVisionStatement = (categoryId: string) => {
    api.get("client/vision_statement_create/" + categoryId).then(response => {
      if (response.json.status === "ok") {
        navigate("/dev-plan/long-term-goal/" + response.json.id);
      }
    })
  }

  const renderCustomerCategoryNoSurvey = (customerCategory: CustomerCategoryInterface) => {

    const hasVisionStatements = customerCategory.visionStatements.length > 0;
    if (!hasVisionStatements) mainButtons++;
    const showOnGoing = surveyStatus.indexOf(customerCategory.id) < 0;
    const showVisionStatements = customerCategory.visionStatements
      .filter(a => {
        if (showOnGoing) {
          return a.progress !== 100;
        } else {
          return a.progress === 100;
        }
      })

    return (
      <div key={"category_" + customerCategory.id} className="mt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-indigo-900">
              <img src={iconPlant} alt="ico" className="h-6 w-6"/>
            </div>
            <p className="z-h3 ml-2">{showLanguageString(lang, customerCategory.name_sv, customerCategory.name)}</p>
          </div>

          {
            hasVisionStatements &&
              <div>
                  <CustomButton
                      buttonType="transparentBase"
                      className="mt-4 mx-auto cursor-pointer"
                      size="sm"
                      onClick={() => createVisionStatement(customerCategory.id)}
                      leftIcon={<img src={iconPlus} alt="info" className="h-4 w-4"/>}
                      style={{width: "unset"}}
                  >
                    {lang === "sv" ? "Lägg till mål" : "Add new goal"}
                  </CustomButton>
              </div>
          }
        </div>

        <div className="mt-3">
          <TabMenu
            active={showOnGoing ? 0 : 1}
            items={[
              {
                title: t("ongoing"),
                callback: () => changeTab(customerCategory.id, 0),
              },
              {
                title: t("completed"),
                callback: () => changeTab(customerCategory.id, 1),
              },
            ]}
          />
        </div>

        {
          showVisionStatements.length > 0 ? (
            <>
              {
                showVisionStatements
                  .sort((a, b) => sortVisionStatements(a, b))
                  .map(visionStatement => {
                    if (visionStatement.status === VisionStatementStatus.Saved) {
                      mainButtons++;
                    }
                    return <div key={"vs_a_" + visionStatement.id}>
                      <VisionStatementSummary visionStatement={visionStatement} nr={0} mainButtons={mainButtons}/>
                    </div>
                  })
              }
            </>
          ) : (
              showOnGoing ? (
                <div className="rounded-md border border-indigo-700 border-dashed p-8 mt-3">
                  <div className="text-center">
                    <img src={iconCircleRings} alt="rings" className="h-8 w-8 mx-auto"/>
                    <p className="mt-4">
                      {
                        !hasVisionStatements ? lang === "sv" ? "Börja din utvecklinsresa" : "Start your development journey" :
                          lang === "sv" ? "Inga pågående mål" : "No ongoing goals"
                      }
                      {/*{showLanguageString(lang, customerCategory.intro_create_vs_sv, customerCategory.intro_create_vs_en)}*/}
                    </p>
                    <CustomButton buttonType={mainButtons > 1 ? "outlineWhiteBase" : "blueBase"}
                                  className="mt-4 mx-auto"
                                  style={{width: "unset"}}
                                  onClick={() => createVisionStatement(customerCategory.id)}>
                      {t("create")} {t("vision_statement")}
                    </CustomButton>
                  </div>
                </div>
              ) : (
                renderThingsWillAppear(lang === "sv" ? "mål" : "goals")
              )

          )
        }

      </div>
    )
  }

  const showSelectDrafts = () => {
    if (!selectDrafts) {
      alert("Error - missing survey");
      return null;
    }
    return (
      <Modal isOpen={true} onClose={() => setSelectDrafts(null)} size="wide">
        <ModalHeader onClose={() => setSelectDrafts(null)}>
          {lang === "sv" ? "Nytt mål" : "New goal"}
        </ModalHeader>
        <ModalBody>
          <VisionStatementDrafts surveyId={selectDrafts.id} update={() => {
            getData();
            setSelectDrafts(null);
          }}/>
        </ModalBody>
      </Modal>
    )
  }

  const sortVisionStatements = (a: VisionStatementInterface, b: VisionStatementInterface) => {
    // Complex! Sort items without deadline last if completed, else first
    // Sort saved before created
    // Sort completed with oldest deadline last
    // Sort ongoing with oldest deadline first
    if (a.status !== b.status) return a.status > b.status ? -1 : 1;
    if (!a.deadline) return a.progress === 100 ? 1 : -1;
    if (!b.deadline) return -1;
    if (a.progress === 100 && b.progress !== 100) return 1;
    if (a.progress !== 100 && b.progress === 100) return -1;
    if (a.progress === 100 && b.progress === 100) {
      return a.deadline > b.deadline ? -1 : 1;
    } else {
      return a.deadline > b.deadline ? 1 : -1;
    }
  }

  const renderVisionStatements = (survey: SurveyInterface) => {
    let hasStarted = false;
    const showCompleted = surveyStatus.indexOf(survey.id) > -1;
    for (const visionStatement of survey.visionStatements) {
      if (visionStatement.status !== VisionStatementStatus.None) {
        hasStarted = true;
        break;
      }
    }

    if (!hasStarted && surveyStatus.indexOf(survey.id) > -1) {
      // Tab "completed"
      return renderThingsWillAppear(lang === "sv" ? "mål" : "goals");
    }

    if (!hasStarted) {
      mainButtons++;
      return (
        <div className="text-center  rounded-md border border-indigo-700 border-dashed p-8 mt-3">
          <img src={iconCircleRings} alt="star" className="h-8 w-8 mx-auto"/>
          <p className="mt-4">
            {lang === "sv" ? "Skapa mål baserat på ditt resultat" : "Create goal based on your result"}
          </p>
          <CustomButton buttonType={mainButtons < 2 ? "blueBase" : "outlineWhiteBase"} className="mt-4 mx-auto" style={{width: "unset"}}
                        onClick={() => setSelectDrafts(survey)}>
            {lang === "sv" ? "Lägg till mål" : "Add goal"}
          </CustomButton>
        </div>
      )
    }
    const visionStatements = survey.visionStatements
      .filter(vs => vs.status !== VisionStatementStatus.None)
      .filter(vs  => (showCompleted && vs.progress === 100 || !showCompleted && vs.progress !== 100))
    ;
    if (visionStatements.length === 0) {
      if (showCompleted) {
        return renderThingsWillAppear(lang === "sv" ? "mål" : "goals");
      } else {
        return (
            <div className="text-center my-8">
              <img src={iconCircleRings} alt="rings" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {
                  lang === "sv" ? "Inga pågående mål" :
                      "No ongoing goals"
                }
              </p>
              <CustomButton buttonType={mainButtons < 2 ? "blueBase" : "outlineWhiteBase"} className="mt-4 mx-auto" style={{width: "unset"}}
                            leftIcon={<img src={iconPlusBlack} alt="plus"/>}
                            onClick={() => setSelectDrafts(survey)}>
                {lang === "sv" ? "Lägg till mål" : "Add new goal"}
              </CustomButton>
            </div>
        )
      }

    }

    return (
      <>
        {
          visionStatements.sort((a, b) => sortVisionStatements(a, b))
            .map(visionStatement => {
              // Toggled by tab selection
              if (visionStatement.status === VisionStatementStatus.Saved) {
                mainButtons++;
              }
              return (
                <div key={"vs_b_" + visionStatement.id}>
                  <VisionStatementSummary visionStatement={visionStatement} nr={0} mainButtons={mainButtons}/>
                </div>
              )
            })
        }
      </>
    )
  }

  const renderMainButton = (txt: string, link: string) => {
    mainButtons++;
    return (
      <CustomButton buttonType={mainButtons < 2 ? "blueBase" : "outlineWhiteBase"}
                    className="mt-4 mx-auto" style={{width: "unset"}}
                    rightIcon={<img src={mainButtons < 2 ? iconArrowRight : iconArrowWhiteRight} alt="right"/>}
                    onClick={() => navigate(link)}>
        {txt}
      </CustomButton>
    )
  }

  const renderThingsWillAppear = (type: string) => {
    return (
      <div className="text-center rounded-md border border-indigo-700 border-dashed p-8 mt-3">
        <img src={iconActivities} alt="star" className="h-6 w-6 mx-auto"/>
        <p className="mt-4">
          {lang === "sv" ? "Dina färdiga" : "Your completed"}
          &nbsp;{type}
          &nbsp;{lang === "sv" ? "kommer visas här." : "will show up here."}
        </p>
      </div>
    )
  }

  const renderSurveyRequest = (survey?: SurveyInterface,
                               surveyRequest?: SurveyRequestInterface) => {
    if (survey) {
      if (surveyStatus.indexOf(survey.id) > -1) {
        return renderThingsWillAppear(lang === "sv" ? "mål" : "goals");
      } else {
        if (surveyRequest && surveyRequest.completed) {
          return (
            <div className="text-center rounded-md border border-indigo-700 border-dashed p-8 mt-3">
              <img src={iconCircleRings} alt="star" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {lang === "sv" ? "Skapa mål baserat på ditt resultat" : "Create goal based on your result"}
              </p>
              <CustomButton buttonType={mainButtons < 2 ? "blueBase" : "outlineWhiteBase"} className="mt-4 mx-auto" style={{width: "unset"}}
                            leftIcon={<img src={iconPlusBlack} alt="plus"/>}
                            onClick={() => setSelectDrafts(survey)}>
                {lang === "sv" ? "Lägg till mål" : "Add goal"}
              </CustomButton>
            </div>
          )
        } else if (!surveyRequest || survey.pendingResponseId || survey.canInvite || surveyRequest.status === "failed") {
          return (
            <div className="text-center rounded-md border border-indigo-700 border-dashed p-8 mt-3">
              <img src={iconScale} alt="star" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {lang === "sv" ? "Se var du står - och var du kan utvecklas" : "See where you stand - and where to grow"}
              </p>
              {renderMainButton(lang === "sv" ? "Gå till mätningar" : "Go to Assessments", "/assessments")}
            </div>
          )
        } else {
          return (
            <div className="text-center rounded-md border border-indigo-700 border-dashed p-8 mt-3">
              <img src={iconScale} alt="star" className="h-8 w-8 mx-auto"/>
              <p className="mt-4">
                {
                  lang === "sv" ? "Din insiktsrapport är snart redo." : "Your insights report is pending."
                }

              </p>
              <p className="mt-2">
                {
                  lang === "sv" ? "Vi meddelar dig via e-post när det är klart, troligen senast " :
                    "We'll notify you by email when it's ready, likely by "
                }
                {surveyRequest.deadline.substring(0, 10)}</p>
            </div>
          )
        }
      }
    }

    return (
      <div>
        Nothing here!
      </div>
    );
  }

  const renderOngoing = () => {

    const todayDate = new Date()
    const showOngoingActivities = ongoingActivities.filter(a => (showActiveStandalone ? !a.completed : a.completed))
    return (
      <>
        <div className="flex items-center mt-6">
          <div className="p-2 rounded-lg bg-indigo-900">
            <img src={iconAi} alt="ico" className="h-6 w-6"/>
          </div>
          <div className="ml-2 z-h3">{lang === "sv" ? "Fristående AI coaching" : "Standalone AI coaching"}</div>
        </div>

        <div className="mt-3">
          <TabMenu
            active={showActiveStandalone ? 0 : 1}
            items={[
              {
                title: t("ongoing"),
                callback: () => setShowActiveStandalone(true),
              },
              {
                title: t("completed"),
                callback: () => setShowActiveStandalone(false),
              },
            ]}
          />
        </div>

        {
          showOngoingActivities.length === 0 ?
          (
            showActiveStandalone ? (
              <div className="rounded-md border border-indigo-700 border-dashed p-8 mt-3">
                <div className="text-center">
                  <img src={iconAi} alt="ai" className="mt-4 h-6 w-6 mx-auto"/>
                  <p className="mt-4">
                    {
                      ongoingActivities.length > 0 ?
                        lang === "sv" ? "Inga pågående aktiveter" :
                          "No ongoing activities" :
                        lang === "sv" ? "Aktivititeter skapade med Fristående AI coaching visas här" :
                          "Activities created with Standalone AI coaching appear here"
                    }
                  </p>
                </div>
              </div>
            ) : (
              renderThingsWillAppear(lang === "sv" ? "aktiviteter" : "activities")
            )
          ) : (
            <>

              {
                showOngoingActivities.sort((a, b) => {
                  // Complex! Sort items without deadline last if completed, else first
                  // Sort completed with oldest deadline last
                  // Sort not ongoing with oldest deadline first
                  if (!a.deadline) return a.completed ? 1 : -1;
                  if (!b.deadline) return -1;
                  if (a.completed && !b.completed) return 1;
                  if (!a.completed && b.completed) return -1;
                  if (a.completed && b.completed) {
                    return a.deadline > b.deadline ? -1 : 1;
                  } else {
                    return a.deadline > b.deadline ? 1 : -1;
                  }
                })
                  .map(ongoing => {
                    let isDue = false;
                    if (ongoing.deadline && !ongoing.completed) {
                      const deadline = new Date(ongoing.deadline);
                      if (deadline < todayDate) {
                        isDue = true;
                      }
                    }
                    return (
                      <div key={"ongoing_" + ongoing.id}
                           className="bg-indigo-800 border-indigo-700 border p-4 pl-7 mt-2 rounded-md hover:bg-indigo-700 cursor-pointer relative"
                           onClick={() => navigate("/tasks/" + ongoing.id)}>
                        <div className="z-action-color bg-peach-600"></div>
                        <div className="flex justify-between text-gray-400 font-medium">
                          <div className="flex items-center">
                            <div className="text-gray-400 font-medium mr-2">
                              {ongoing.title}
                            </div>
                            {
                              ongoing.completed &&
                                <PlainBadge text={lang === "sv" ? "Klar" : "Completed"} color="green" size="small"/>
                            }
                          </div>

                          {
                            ongoing.deadline &&
                              <div className="text-sm ml-4">
                                  <span className="text-gray-100">{t("due-date")}:&nbsp;</span>
                                  <span className="text-gray-400">{ongoing.deadline.substring(0, 10)}</span>
                              </div>
                          }
                        </div>
                        {
                          isDue &&
                            <AttentionBox message={lang === "sv" ? "Slutdatumet är här - uppdatera din status" :
                              "Due date is here - update progress"} />
                        }
                      </div>
                    )
                  })
              }
            </>
          )
        }
      </>
    )
  }



  return (
    <div className="main-one-col">
      <div className="z-h2 mt-6">{t("long-term-goals")}</div>
      {
        customerCategories.map(customerCategory => {
          if (customerCategory.surveys.length > 0) {
            return renderCustomerCategorySurveys(customerCategory);
          } else {
            return renderCustomerCategoryNoSurvey(customerCategory);
          }

        })
      }
      {selectDrafts && showSelectDrafts()}
      {renderOngoing()}
      <div className="mt-4">&nbsp;</div>
    </div>
  );
}

export default DevelopmentPlan;